import type {
  ObjectToolbarConfig,
  RegisterToolbarConfigArgs,
  ToolbarActionGroupConfig,
} from '@/blocks/object-toolbar';

/**
 * Enterprise risk's register builder takes the shared
 * {@link RegisterToolbarConfigArgs} plus one module-specific extra: the bespoke
 * "Add risk to legal entity" (instantiate) bulk action, which has no analogue in
 * the shared shape. It is surfaced in the toolbar's overflow ⋮ menu, gated on
 * `insert:enterprise_risk` and disabled until register rows are selected.
 */
export interface EnterpriseRiskRegisterToolbarConfigArgs extends RegisterToolbarConfigArgs {
  instantiate?: {
    label: string;
    onClick: () => void;
    /** true → no register rows selected, so the bulk action is disabled. */
    disabled?: boolean;
  };
}

/**
 * Assemble the object-toolbar config for the Enterprise risk register/list page
 * (CUS-167). Register toolbars are type-level: no `parentObject` (permissions
 * resolve against the type) and no `save` (a list page has no form). The
 * register's multi-format export (CSV / PDF / custom PDF) is surfaced as the
 * toolbar's "Download" group — one action per `ExportOption` returned by
 * `useExportOptions`, the same source that drives the legacy `ExportButton`.
 * The create button is surfaced as the toolbar's primary action, gated on
 * `insert:enterprise_risk` (matching the legacy register `<Permission>`, which
 * passes no `canHaveAccessAsContributor`). Instantiate + the relocated ribbon
 * "Edit Filters" action live in the overflow ⋮ menu.
 *
 * Pure so it can be unit-tested without rendering the page.
 */
export const buildEnterpriseRiskRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  create,
  editRibbon,
  instantiate,
}: EnterpriseRiskRegisterToolbarConfigArgs): ObjectToolbarConfig => {
  const overflow: ToolbarActionGroupConfig[] = [];
  if (editRibbon) {
    overflow.push({
      id: 'edit',
      label: editRibbon.groupLabel,
      actions: [
        {
          id: 'edit-ribbon',
          label: editRibbon.label,
          iconName: 'filter-lines',
          onClick: editRibbon.onClick,
        },
      ],
    });
  }
  if (instantiate) {
    overflow.push({
      id: 'instantiate',
      actions: [
        {
          id: 'instantiate',
          label: instantiate.label,
          iconName: 'file-plus-02',
          onClick: instantiate.onClick,
          disabled: instantiate.disabled,
          permission: 'insert:enterprise_risk',
        },
      ],
    });
  }

  return {
    header: { title, counter },
    actions: {
      ...(create
        ? {
            primary: [
              {
                id: 'create',
                label: create.label,
                iconName: 'plus',
                onClick: create.onClick,
                permission: 'insert:enterprise_risk',
              },
            ],
          }
        : {}),
      ...(overflow.length > 0 ? { overflow } : {}),
      export: [
        {
          id: 'export',
          actions: exportOptions.map((option) => ({
            id: option.id,
            label: option.text,
            iconName: 'download-01',
            onClick: option.exportFunction,
            disabled: option.disabled,
          })),
        },
      ],
    },
  };
};
